import React, {useContext, useRef, useState} from 'react';
import './myDocuments.scss';
import MyDoocumentTabs from './MyDoocumentTabs';
import Loading from '../Loading/Loading';
import {customerDataContext} from '../../contexts/CustomerDataProvider';
import {gql} from 'apollo-boost';
import {useQuery} from "@apollo/react-hooks";
import {getDownloadUrl} from './documentDownloader';
import {useCurrentUser} from '../../contexts/CurrentUserProvider';
import Modal from '../Modal/Modal';
import successImg from '../../assets/images/success.png';
import errorImg from '../../assets/images/error.png';
import {MONTHS} from "../../utils/helpers";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

/** @import {GetDocumentsResponse, MyDocumentsProps, Document} from './MyDocumentsTypes' */
/** @import {QueryResult} from '@apollo/react-common' */
/** @import {Account} from "../../contexts/CustomerDataProviderTypes" */

const GET_DOCUMENTS = gql`
    query GetDocuments {
        documents {
            documentName
            accountNumber
            createdDate
            downloadLink
            category
        }
    }  
`;

function filterByDocumentType(/** @type {Document[]} */ docs) {
	if (!docs)
		return {
			statements: [],
			interestCertificates: [],
			other: []
		};

	return {
		statements: docs.filter(d => d.category === 'STATEMENT'),
		interestCertificates: docs.filter(d => d.category === 'INTEREST_CERTIFICATE'),
		other: docs.filter(d => d.category === 'OTHER')
	};
};

function MyDocuments(/** @type {MyDocumentsProps} */ { statements, certificates }) {
	const [page, setPage] = useState('statements');
	const [downloadUrl, setDownloadUrl] = useState('');
	const [downloadFileName, setDownloadFileName] = useState('');
	const downloadHost = useRef(/** @type {null | HTMLAnchorElement} */ (null));
	let customerData = useContext(customerDataContext);
	let currentUser = useCurrentUser();
	const [modalLoadingOpen, setModalLoadingOpen] = useState(false);
	const [modalSuccessOpen, setModalSuccessOpen] = useState(false);
	const [modalErrorOpen, setModalErrorOpen] = useState(false);
	const [modalErrorMessage, setModalErrorMessage] = useState('Sorry, we couldn’t download your document.');

	const analytics = useUnifiedAnalytics();

	// get documents
	/** @type {QueryResult<GetDocumentsResponse>} */
	const { data, loading } = useQuery(GET_DOCUMENTS, {
		skip: !customerData || customerData.loading || !customerData.data
	});

	// show loading while the page is loading
	if (!currentUser || !customerData || customerData.loading || !customerData.data || !data || loading)
		return <Loading message="We're loading your documents..." />;

	// build grouped document object
	var filteredDocuments = filterByDocumentType(data.documents);

	const downloadClientDocument = async (/** @type {string} */ documentUrl, /** @type {string} */ downloadFileName,/** @type {string} */ downloadType) => {
		if (!currentUser.accessToken) return;

		try {
			const result = await getDownloadUrl(documentUrl, currentUser.accessToken);
			resetAllModals();
			if (result) {
				onSuccessOpen();
				setDownloadUrl(result);
				setDownloadFileName(downloadFileName);

				// React state updates are asynchronous. Set the anchor properties before
				// clicking it; otherwise the first click uses the previous href (the
				// dashboard page) and downloads HTML. The next click then uses the PDF
				// URL left by this click.
				if (downloadHost.current) {
					downloadHost.current.href = result;
					downloadHost.current.download = downloadFileName;
					downloadHost.current.click();
				}

				analytics.trackAnalyticsDownloadEvent(
					'download file',
					{ url: documentUrl, region: 'other', type: 'button',action: 'download',
						productInfo: {
							productName: '',
							id: ''
						}
					},
					{ fileName: downloadFileName, fileDescription: page },
					`MyDocument|download_click|${downloadType}`
				);

			}
			else {
				onErrorOpen();
			}
		}
		catch {
			resetAllModals();
			onErrorOpen();
		}
	};

	const viewClientDocument = async (/** @type {string} */ documentUrl) => {
		if (!currentUser.accessToken)
			throw Error('Please sign in again and try viewing your document.');

		const result = await getDownloadUrl(documentUrl, currentUser.accessToken);
		if (!result)
			throw Error('Sorry, we couldn’t load your document.');

		window.open(result, '_blank', 'noopener,noreferrer');
	};

	const resetAllModals = () => {
		setModalLoadingOpen(false);
		setModalSuccessOpen(false);
		setModalErrorOpen(false);
	};

	const onLoadingOpen = () => {
		setModalLoadingOpen(true);
	};

	const onSuccessOpen = () => {
		setModalSuccessOpen(true);
	};

	const onErrorOpen = (/** @type {string} */ message = 'Sorry, we couldn’t download your document.') => {
		setModalErrorMessage(message);
		setModalErrorOpen(true);
	};

	const renderDownloadModal = () => {
		return (
			<Modal onClose={resetAllModals}>
				<Loading message="Downloading..." />
			</Modal>
		);
	};

	const renderSuccessDownloadModal = () => {
		return (
			<Modal onClose={resetAllModals}>
				<img src={successImg} className='my-documents__image' />
				<h4 className="my-documents__title">Your download is complete</h4>
				<p className="my-documents__slim">Your statement should be in your downloads folder, or in the folder where your downloads normally go. If you can’t find it, disable any pop-up blockers and try again.</p>
			</Modal>
		);
	};

	const renderErrorDownloadModal = () => {
		return (
			<Modal onClose={resetAllModals}>
				<img src={errorImg} className='my-documents__image' />
				<h4 className="my-documents__title">Please try again</h4>
				<p className="my-documents__slim">{modalErrorMessage}</p>
			</Modal>
		);
	};

	// set callback for download interest certs
	filteredDocuments.interestCertificates = filteredDocuments.interestCertificates.map(v => {
		v.downloadAction = (/** @type {Event} */ e) => {
			e.preventDefault();
			onLoadingOpen();
			downloadClientDocument(v.downloadLink, `${v.documentName}.pdf`,'interest certificate');
		}
		v.viewAction = async (/** @type {Event} */ e) => {
			e.preventDefault();
			await viewClientDocument(v.downloadLink);
		}

		return v;
	});

	const trackDownload = (/** @type {string} */ downloadUrl, /** @type {string} */ downloadFileName, /** @type {string} */ page) => {
		analytics.trackAnalyticsDownloadEvent(
			'download file',
			{ url: downloadUrl, region: 'other', type: 'button',action: 'download',
				productInfo: {
					productName: '',
					id: ''
				} 
			},
			{ fileName: downloadFileName, fileDescription: page },
			'MyDocument|download_click|other'
		);

	}

	const openLink = (/** @type {string} */ downloadLink, /** @type {string} */ documentName, /** @type {string} */ type, /** @type {() => void} */ action) => (/** @type {Event} */ e) => {
		e && e.preventDefault && e.preventDefault();
		trackDownload(downloadLink, documentName, type);
		window.open(downloadLink, '_blank');
		action && action();
	}

	const viewLink = (/** @type {string} */ downloadLink) => (/** @type {Event} */ e) => {
		e && e.preventDefault && e.preventDefault();
		window.open(downloadLink, '_blank', 'noopener,noreferrer');
	}

	filteredDocuments.statements = filteredDocuments.statements.map(v => {
		v.downloadAction = (/** @type {Event} */ e) => {
			e.preventDefault();
			onLoadingOpen();
			downloadClientDocument(v.downloadLink, `${v.documentName}.pdf`,'statement');
		}
		v.viewAction = async (/** @type {Event} */ e) => {
			e.preventDefault();
			await viewClientDocument(v.downloadLink);
		}
		return v;
	});

	const getLatestStatement = (/** @type {Account} */ account) => async (/** @type {Event} */ e) => {
		e.preventDefault();
		onLoadingOpen();
		const date = new Date();
		await downloadClientDocument(
			`/account/${account.id}/statement/latest/download`,
			`${account.product.name} - STATEMENT ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}.pdf`.toUpperCase(),
			'statement');
	};

	filteredDocuments.other = filteredDocuments.other.map(v => {
		// Special handling for ISA Declaration documents
		if (v.documentName && v.documentName.includes('ISA Declaration')) {
			v.downloadAction = (/** @type {Event} */ e) => {
				e.preventDefault();
				onLoadingOpen();
				// Use the existing downloadLink which already contains the correct endpoint
				downloadClientDocument(
					v.downloadLink,
					`${v.documentName}.pdf`,
					'isa-declaration'
				);
			};
			v.viewAction = async (/** @type {Event} */ e) => {
				e.preventDefault();
				await viewClientDocument(v.downloadLink);
			};
		} else {
			// Keep existing functionality for other documents
			v.downloadAction = openLink(v.downloadLink, `${v.documentName}.pdf`, 'other', () => {});
			v.viewAction = viewLink(v.downloadLink);
		}
		return v;
	});

	const documentTabs = [
		{
			key: 'statements',
			name: 'Statements',
			content: filteredDocuments.statements,
			noContent: 'Your annual statements will be available to download here.',
			buttonAction: getLatestStatement
		},
		{
			key: 'interest',
			name: 'Interest certificates',
			content: filteredDocuments.interestCertificates,
			noContent: 'Your interest certificate will be available to download here after the end of the tax year (5 April).'
		},
		{
			key: 'other',
			name: 'Other',
			content: filteredDocuments.other,
			noContent: 'Any other documents will be available to download here.'
		}
	];

	return (
		<div className="my-documents">
			<div className="my-documents__title">My documents</div>
			<div className="my-documents__content">
				<MyDoocumentTabs tabs={documentTabs} selected={page} onSelect={setPage} />
			</div>
			{modalLoadingOpen && renderDownloadModal()}
			{modalSuccessOpen && renderSuccessDownloadModal()}
			{modalErrorOpen && renderErrorDownloadModal()}
			<a className="my-documents__download-host" target="_blank" href={downloadUrl} download={downloadFileName} ref={downloadHost} rel="noreferrer">Download {downloadUrl}</a>
		</div>
	);
}

export default MyDocuments;
